import { Grid, IconButton, Stack, Tooltip } from '@mui/material';
import { GridRowsProp } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../app-context';
import IDialogComponent from '../../components/dialog/i-dialog.component';
import IGridComponent from '../../components/grid/i-grid.component';
import SearchPanel from '../../components/search-panel/search-panel.component';
import IDropdown from '../../components/text-component/dropdown/i-dropdown.component';
import IDateField from '../../components/text-component/i-datefield/i-datefield.component';
import ITextField from '../../components/text-component/textfield/i-textfield.component';
import TitlePanel from '../../components/title-panel/title-panel.component';
import { ALL, COSTING_TYPE_SELECT_ITEMS, DAYS_YEAR, GET_ALL_CUSTOMER_URL, GET_ALL_PRODUCT_URL, PAGE_SIZE } from '../common/constants';
import { CustomGridColDef, formatDate, formatTo2Decimal, formatToNumberRound, isEmptyArray } from '../common/util.services';
import SplitComponent from './cost-split/split.component';
import './costing-list.component.scss';
import { searchCosting } from './costing.services';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ICheckbox from '../../components/checkbox/i-checkbox.component';
import { HelpOutline } from '@mui/icons-material';
import { getEnquiryProduct } from '../enquiry/enquiry.services';
import ProductDetailComponent from '../enquiry/enq-product/product-detail.component';

const CostingComponent = () => {
  const appContext = useContext(AppContext);
  const navigate = useNavigate();

  const showSplitDialog = () => {
    setRowData(searchData);
    setOpen(true);
  }

  const [amazon, setAmazon] = useState(true);
  const [openPrdDetail, setOpenPrdDetail] = useState(false);
  const [prdDetail, setPrdDetail] = useState(null);
  const [searchData, setSearchData] = useState<any>({customer: ALL, 
                                                     product: ALL, 
                                                     type: ALL, 
                                                     startDate: dayjs().subtract(DAYS_YEAR, 'day'), 
                                                     endDate: dayjs()});
  const rows: GridRowsProp = [];
  const [costingList, setCostingList] = useState([{rowId: '', customer: 'Loading...'}]);
  const [rowData, setRowData] = useState(null);
  const [open, setOpen] = useState(false);
  const popperProps = {sx: {'.MuiTooltip-tooltip': {bgcolor: 'ivory', fontSize: 16, color: 'black', border: 1}}};
  const columns: CustomGridColDef[] = [
    { field: 'customer', headerName: 'Customer', filterable: false, sortable: false, width: 105 },
    { field: 'product', headerName: 'Product', filterable: false, sortable: false, width: 80 },
    { field: 'type', headerName: 'Type', filterable: false, sortable: false, width: 80 },
    { field: 'code', headerName: 'Code', filterable: false, sortable: false, width: 120,
      renderCell: (params) => { return <div className="cell-link"
      onClick={() => { openCostingPage(params.row) }}>{params.row.code}</div> }
    },
    { field: 'enquiryCode', headerName: 'Enq Code', filterable: false, sortable: false, width: 120,
      renderCell: (params) => { return <div className="cell-link"
      onClick={() => { openEnquiryCostingPage(params.row) }}>{params.row.enquiryCode}</div> }
    },
    { field: 'costingDate', headerName: 'Cos Date', filterable: false, sortable: false, 
      valueFormatter: (costingDate: any) => formatDate(costingDate), width: 90 },
    { field: 'description', headerName: 'Description', filterable: false, sortable: false, width: 130,
      renderCell: (params) => (<Stack direction="row" alignItems="center" gap={0}>
                                <IconButton size="small" onClick={() => openPrdDetailPage(params.row.enqProductId)}
                                  sx={{ p: 0.6, ml: -1 }}> <HelpOutline fontSize="small" color='secondary'/>
                                </IconButton>{params.row.description}</Stack>)
    },
    { field: 'inputCost', headerName: 'IP Cost', filterable: false, sortable: false, 
      valueFormatter: (inputCost: any) => formatToNumberRound(inputCost), width: 70 },
    {
      field: 'contingencyPercentage', headerName: 'Conti', filterable: false, sortable: false,
      valueFormatter: (contingencyPercentage: any) => formatTo2Decimal(contingencyPercentage) + '%', width: 70
    },
    {
      field: 'financeCost', headerName: 'Finance', filterable: false, sortable: false,
      valueFormatter: (financeCost: any) => formatTo2Decimal(financeCost) + '%', width: 70
    },
    {
      field: 'commissionPercentage', headerName: 'Comm', filterable: false, sortable: false,
      valueFormatter: (commissionPercentage: any) => formatTo2Decimal(commissionPercentage) + '%', width: 70
    },
    { field: 'totalCost', headerName: 'Price', filterable: false, sortable: false, 
      valueFormatter: (totalCost: any) => formatToNumberRound(totalCost), width: 70 },
    {
      field: 'marginPercentage', headerName: 'Gross', filterable: false, sortable: false,
      valueFormatter: (marginPercentage: any) => formatTo2Decimal(marginPercentage) + '%', width: 80,
      cellClassName: (params) => { return params.row.marginPercentage > 0 ? 'Positive' : 'Negative' }
    },
    {
      field: 'netMarginPct', headerName: 'Net', filterable: false, sortable: false,
      valueFormatter: (netMarginPct: any) => formatTo2Decimal(netMarginPct) + '%', width: 80,
      cellClassName: (params) => { return params.row.netMarginPct > 0 ? 'Positive' : 'Negative' }
    },
    {
      field: 'order', headerName: 'Order', filterable: false, sortable: false, width: 60,
      renderCell: (params) => { return <div className="cell-link" 
      onClick={() => { openCostingOrderPage(params.row) }}>{params.row.order}</div> }
    },
    {
      field: 'split', filterable: false, sortable: false, width: 20, 
      headerIcon: <CallSplitIcon fontSize='small'/>, headerClickAction: showSplitDialog, headerIconText: 'Total Cost Split',
      renderCell: (params) => { return <div onClick={() => {openSplitPopup(params)}} 
                                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
                                         width: '100%', height: '100%',cursor: 'pointer'}}>
                              {<Tooltip title={'Cost Split'} PopperProps={popperProps}>
                               <CallSplitIcon fontSize='small' color="primary"/></Tooltip>} </div>}
    }
  ];
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    if (isEmptyArray(rows)) {
      setLoading(true);
      searchData.amazon = amazon;
      searchCosting(searchData, page, pageSize, appContext,
        (response: any) => {
          setCostingList(response.data.data);
          setTotalRows(response.data.totalElements);
          appContext.setShowLoader(false);
          setLoading(false);
        }, (error: any) => {
          console.log(error);
          appContext.setShowLoader(false);
          setLoading(false);
        });
    }
  }, [searchData, page, pageSize]);

  const openEnquiryCostingPage = (row: any) => {
    navigate('/costing-list/enq/' + row.enquiryCode)
  }

  const closePrdDetail = () => {
    setOpenPrdDetail(false);
  }

  const openPrdDetailPage = (prdId: string) => {
    getEnquiryProduct(prdId, appContext,
      (response: any) => {
        appContext.setShowLoader(false);
        setPrdDetail(response.data);
        setOpenPrdDetail(true);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message);
      });
  }

  const openCostingPage = (row: any) => {
    navigate('/costing/list/' + row.rowId, {state: {enquiryCode: "", 
                                                    customer: "", 
                                                    product: "", 
                                                    type: "", 
                                                    description: "", 
                                                    setQty: row.pack}})
  }

  const openCostingOrderPage = (row: any) => {
    navigate('..//enquiry/order/cos/' + row.code)
  };

  const search = (data: any) => {
    setSearchData(data);
  }

  const openSplitPopup = (params: any) => {
    setRowData(params.row);
    setOpen(true);
  }

  const handleClose = () => {
    setOpen(false);
  }

  const onPaginationModelChange = (evt: any) => {
    setPage(evt.page);
    setPageSize(evt.pageSize);
  }
  
  const getSearchComponent = () => {
    return <SearchPanel search={search} formData={searchData}>
      <Grid container spacing={1} alignItems="center">
        <Grid xs={1.5}>
          <ITextField name='searchText' label='Search Text' />
        </Grid>
        <Grid xs={1.5}>
          <IDropdown name='customer' label='Customer' url={GET_ALL_CUSTOMER_URL} allItemRequired={true}/>
        </Grid>
        <Grid xs={1}>
          <ICheckbox name='amazon' label='Amazon' defaultChecked={true}
            onChangeEvent={(e: any) => {setAmazon(e.target.checked)}}/>
        </Grid>
        <Grid xs={2}>
          <IDropdown name='product' label='Product' url={GET_ALL_PRODUCT_URL} allItemRequired={true} />
        </Grid>
        <Grid xs={2}>
          <IDropdown name='type' label='Type' itemList={COSTING_TYPE_SELECT_ITEMS} allItemRequired={true} />
        </Grid>
        <Grid xs={2}>
          <IDateField name='startDate' label='Start Date' />
        </Grid>
        <Grid xs={2}>
          <IDateField name='endDate' label='End Date' />
        </Grid>
      </Grid></SearchPanel>
  }

  const getMainPanel = () => {
    return <>
      {getSearchComponent()}
      <IGridComponent rows={costingList} columns={columns} loading={loading} showAddBtn={false} showDeleteAction={false} 
        paginationType="server" paginationModelChange={onPaginationModelChange} totalRows={totalRows} showEditAction={false}
        showAction={false} showApproveAction={false} getRowId={(row: any) => row.rowId}
        showExtBtn={false} extBtnAction={showSplitDialog} extBtnText={'Split'}/>
    </>
  }
  
  return <>
    <TitlePanel title='Costing' panelBody={getMainPanel()} />
    <IDialogComponent onClose={handleClose} open={open} title='Cost Split'
      dialogBody={<SplitComponent data={rowData} />} />
    <IDialogComponent onClose={closePrdDetail} open={openPrdDetail} title={'Product Details'}
        dialogBody={<ProductDetailComponent readOnly={true} data={prdDetail} close={closePrdDetail}/>} />
  </>
}

export default CostingComponent;